// Hook: Order management — place, modify, cancel, GTT
import { useState, useCallback } from 'react';
import { useTrading } from '../context/TradingContext';
import angelOneApi from '../services/angelOneApi';

export function useOrderManager() {
  const { placeOrder, modifyOrder, cancelOrder, refreshPortfolio } = useTrading();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const submitOrder = useCallback(async (orderData) => {
    setLoading(true);
    setError(null);
    try {
      const result = await placeOrder(orderData);
      setLastResult(result);
      return result;
    } catch (err) {
      setError(err.message);
      return { status: false, message: err.message };
    } finally {
      setLoading(false);
    }
  }, [placeOrder]);

  const modifyExisting = useCallback(async (orderData) => {
    setLoading(true);
    setError(null);
    try {
      const result = await modifyOrder(orderData);
      setLastResult(result);
      return result;
    } catch (err) {
      setError(err.message);
      return { status: false, message: err.message };
    } finally {
      setLoading(false);
    }
  }, [modifyOrder]);

  const cancelExisting = useCallback(async (orderId, variety = 'NORMAL') => {
    if (!orderId) {
      const msg = 'Cannot cancel: missing order ID';
      setError(msg);
      return { status: false, message: msg };
    }
    setLoading(true);
    setError(null);
    try {
      const result = await cancelOrder(orderId, variety || 'NORMAL');
      setLastResult(result);
      // Check if API returned an error
      if (result?.status === false) {
        setError(result.message || 'Cancel failed');
      }
      return result;
    } catch (err) {
      const msg = err.message || 'Failed to cancel order';
      setError(msg);
      return { status: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, [cancelOrder]);

  // GTT
  const createGTT = useCallback(async (gttData) => {
    setLoading(true);
    setError(null);
    try {
      const result = await angelOneApi.createGTT(gttData);
      await refreshPortfolio();
      setLastResult(result.data);
      return result.data;
    } catch (err) {
      setError(err.message);
      return { status: false, message: err.message };
    } finally {
      setLoading(false);
    }
  }, [refreshPortfolio]);

  const cancelGTT = useCallback(async (ruleId, token, exchange) => {
    setLoading(true);
    try {
      const result = await angelOneApi.cancelGTT({ id: ruleId, symboltoken: token, exchange });
      await refreshPortfolio();
      return result.data;
    } catch (err) {
      setError(err.message);
      return { status: false, message: err.message };
    } finally {
      setLoading(false);
    }
  }, [refreshPortfolio]);

  const listGTT = useCallback(async (statuses = ['NEW', 'ACTIVE']) => {
    try {
      const result = await angelOneApi.listGTT({ status: statuses, page: 1, count: 50 });
      return result.data?.data || [];
    } catch (err) {
      return [];
    }
  }, []);

  return {
    submitOrder, modifyExisting, cancelExisting,
    createGTT, cancelGTT, listGTT,
    loading, error, lastResult,
  };
}
